/** @jsxImportSource theme-ui */
import React from 'react';
import { useSelector } from 'react-redux';

import { InsightsState } from 'app-redux/InsightsState';
import { getRiskLikelihood, getRiskMagnitude } from 'utils/modelHelpers';

import { Typography } from 'components/_common/Typography';
import BdPngExportHeader from 'components/_controls/PngExports/BdPngExportHeader';

import locationsSelectors from 'locations/locations.selectors';
import RiskOverviewImportationDescription from 'riskOverview/components/card/RiskOverviewImportationDescription';
import RiskOverviewImportationSummary from 'riskOverview/components/card/RiskOverviewImportationSummary';
import RiskOverviewCardActivityDetails from 'riskOverview/components/RiskOverviewCardActivityDetails';
import riskOverviewSelectors from 'riskOverview/riskOverview.selectors';

import RiskOverviewCardLandscapeContainer from './RiskOverviewCardLandscapeContainer';

type RiskOverviewCardActivityLandscapeProps = {
  header: string;
  locationId: number;
  activityId: number;
};

const RiskOverviewCardActivityLandscape: React.FC<RiskOverviewCardActivityLandscapeProps> = ({
  header,
  locationId,
  activityId,
}) => {
  const riskFeedEntry = useSelector((state: InsightsState) =>
    riskOverviewSelectors.getRiskFeedEntryByActivityIdAndLocationId(state)
  )(activityId, locationId);

  const locationName = useSelector(locationsSelectors.getLocationName(locationId));

  const riskMagnitude =
    riskFeedEntry != null ? getRiskMagnitude(riskFeedEntry.importationRisk) : null;
  const riskLikelihood =
    riskFeedEntry != null ? getRiskLikelihood(riskFeedEntry.importationRisk) : 'Not calculated';

  return (
    <RiskOverviewCardLandscapeContainer>
      <BdPngExportHeader title={header} superTitle="Your risk of" supertitleVariant="overline" />
      <div sx={{ display: 'flex', mt: '32px' }}>
        <div sx={{ width: '60%', pr: '16px' }}>
          <Typography variant="h6" color="stone90" marginBottom="16px">
            Activity in {locationName}
          </Typography>
          <RiskOverviewCardActivityDetails
            locationId={locationId}
            diseaseActivityId={activityId}
            timeFrame="60days"
            noDataLoad
          />
        </div>
        <div sx={{ width: '40%', pl: '16px' }}>
          <Typography variant="h6" color="stone90" marginBottom="16px">
            Risk of Case Importation
          </Typography>
          <RiskOverviewImportationSummary
            title={riskMagnitude ?? '—'}
            subtitle="Estimated case importations per month"
            riskMagnitude={riskMagnitude}
            riskLikelihood={riskLikelihood}
            sx={{ maxWidth: [null, '100%'], width: '100%' }}
          />
          <ul sx={{ width: '100%', m: 0, paddingInlineStart: '18px' }}>
            <li>
              <RiskOverviewImportationDescription
                activityId={activityId}
                locationId={locationId}
                diseaseId={riskFeedEntry.diseaseId}
                riskMagnitude={riskMagnitude}
                sx={{ mt: '32px' }}
              />
            </li>
          </ul>
        </div>
      </div>
    </RiskOverviewCardLandscapeContainer>
  );
};

export default RiskOverviewCardActivityLandscape;
